import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "", make: "", model: "", year: 2024, body_type: "Sedan",
  transmission: "Automatic", fuel: "Petrol", mileage_km: 0,
  horsepower: 0, seats: 4, listing_type: "sale", price: 0,
  description: "", features: "", images: "", location: "", available: true,
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("cars");
  const [cars, setCars] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [song, setSong] = useState({ title: "", artist: "", audio_url: "", cover_url: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const loadCars = () => api.get("/cars").then((r) => setCars(r.data));
  const loadInquiries = () => api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  const loadContacts = () => api.get("/contact").then((r) => setContacts(r.data)).catch(() => {});
  const loadSubscribers = () => api.get("/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  const loadSong = () => api.get("/song-of-the-day").then((r) => setSong(r.data)).catch(() => {});

  useEffect(() => {
    loadCars();
    loadInquiries();
    loadContacts();
    loadSubscribers();
    loadSong();
  }, []);

  const saveSong = async () => {
    try {
      await api.put("/song-of-the-day", song);
      toast.success("Song of the day updated");
    } catch {
      toast.error("Failed to save song");
    }
  };

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      ...c,
      features: (c.features || []).join(", "),
      images: (c.images || []).join(", "),
    });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    const payload = {
      ...form,
      year: parseInt(form.year),
      mileage_km: parseInt(form.mileage_km || 0),
      horsepower: parseInt(form.horsepower || 0),
      seats: parseInt(form.seats || 4),
      price: parseFloat(form.price),
      features: form.features ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : [],
      images: form.images ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (editing === "new") {
        await api.post("/cars", payload);
        toast.success("Car created");
      } else {
        await api.put(`/cars/${editing}`, payload);
        toast.success("Car updated");
      }
      close();
      loadCars();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    await api.delete(`/cars/${id}`);
    toast.success("Deleted");
    loadCars();
  };

  const updateInquiryStatus = async (id, status) => {
    await api.put(`/inquiries/${id}/status`, { status });
    loadInquiries();
  };

  const updateContactStatus = async (id, status) => {
    await api.put(`/contact/${id}/status`, { status });
    loadContacts();
  };

  return (
    <div data-testid="admin-dashboard" className="pt-32 pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="label-caps mb-3">Control Room</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase">
            Admin Dashboard
          </h1>
        </div>
        {tab === "cars" && (
          <button data-testid="admin-new-car" onClick={openNew} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> New Car
          </button>
        )}
      </div>

      <div className="flex gap-8 border-b border-white/5 mb-10">
        {[
          { id: "cars", label: `Inventory (${cars.length})` },
          { id: "inquiries", label: `Inquiries (${inquiries.length})` },
          { id: "contacts", label: `Messages (${contacts.length})` },
          { id: "subscribers", label: `Subscribers (${subscribers.length})` },
          { id: "song", label: "Song of the Day" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`pb-4 text-xs tracking-widest uppercase transition-colors ${
              tab === t.id ? "text-[var(--gold)] border-b-2 border-[var(--gold)]" : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "cars" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cars.map((c) => (
            <div key={c.id} className="bg-[#121212] border border-white/5 p-5 flex gap-4">
              <img src={c.images?.[0]} alt={c.title} className="w-32 h-24 object-cover" />
              <div className="flex-1">
                <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">
                  {c.listing_type} · {c.year}
                </div>
                <div className="font-display font-bold">{c.title}</div>
                <div className="text-sm gold-gradient font-bold mt-1">
                  ${c.price.toLocaleString()}{c.listing_type === "rent" ? "/day" : ""}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button data-testid={`edit-${c.id}`} onClick={() => openEdit(c)} className="p-2 text-gray-400 hover:text-[var(--gold)]"><Edit size={14} /></button>
                <button data-testid={`delete-${c.id}`} onClick={() => remove(c.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-3">
          {inquiries.length === 0 && <div className="text-gray-500 text-xs uppercase tracking-widest">No inquiries yet.</div>}
          {inquiries.map((i) => (
            <div key={i.id} className="bg-[#121212] border border-white/5 p-5 grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
              <div className="md:col-span-2">
                <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">{i.listing_type}</div>
                <div className="font-display font-bold">{i.car_title}</div>
              </div>
              <div>
                <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">From</div>
                <div className="text-sm">{i.user_name}</div>
                <div className="text-xs text-gray-500">{i.user_email}</div>
              </div>
              <div>
                <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">Phone</div>
                <div className="text-sm">{i.phone || "—"}</div>
              </div>
              <div>
                <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">Dates</div>
                <div className="text-sm">{i.start_date ? `${i.start_date} → ${i.end_date}` : "—"}</div>
              </div>
              <select
                data-testid={`status-${i.id}`}
                value={i.status}
                onChange={(e) => updateInquiryStatus(i.id, e.target.value)}
                className="select-dark"
              >
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          ))}
        </div>
      )}

      {tab === "contacts" && (
        <div className="space-y-3">
          {contacts.length === 0 && <div className="text-gray-500 text-xs uppercase tracking-widest">No messages yet.</div>}
          {contacts.map((c) => (
            <div key={c.id} data-testid={`contact-${c.id}`} className="bg-[#121212] border border-white/5 p-5">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                <div>
                  <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">From</div>
                  <div className="font-display font-bold text-sm">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.email}</div>
                  {c.phone && <div className="text-xs text-gray-500">{c.phone}</div>}
                </div>
                <div>
                  <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">Subject</div>
                  <div className="text-sm text-gray-300">{c.subject || "General"}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">Message</div>
                  <div className="text-sm text-gray-300 font-light whitespace-pre-line">{c.message}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">Status</div>
                  <select
                    data-testid={`contact-status-${c.id}`}
                    value={c.status}
                    onChange={(e) => updateContactStatus(c.id, e.target.value)}
                    className="select-dark mt-1"
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="replied">Replied</option>
                  </select>
                  <div className="text-[0.6rem] text-gray-600 mt-2">{new Date(c.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "subscribers" && (
        <div className="space-y-2">
          {subscribers.length === 0 && <div className="text-gray-500 text-xs uppercase tracking-widest">No subscribers yet.</div>}
          {subscribers.map((s) => (
            <div key={s.id} className="bg-[#121212] border border-white/5 p-4 flex justify-between items-center">
              <div className="font-display text-sm">{s.email}</div>
              <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest">
                {new Date(s.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "song" && (
        <div className="max-w-2xl bg-[#121212] border border-white/5 p-8">
          <div className="label-caps mb-2">Home Page Player</div>
          <p className="text-sm text-gray-400 font-light mb-8">
            Set the track that plays in the floating player on the home page. Leave audio URL empty to hide the player.
          </p>
          <div className="space-y-6">
            <div>
              <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Title</label>
              <input
                data-testid="song-title"
                className="input-line"
                placeholder="Track title"
                value={song.title || ""}
                onChange={(e) => setSong({ ...song, title: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Artist</label>
              <input
                data-testid="song-artist"
                className="input-line"
                placeholder="Artist name"
                value={song.artist || ""}
                onChange={(e) => setSong({ ...song, artist: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Audio URL (MP3)</label>
              <input
                data-testid="song-url"
                className="input-line"
                placeholder="https://..."
                value={song.audio_url || ""}
                onChange={(e) => setSong({ ...song, audio_url: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Cover Image URL (optional)</label>
              <input
                className="input-line"
                placeholder="https://..."
                value={song.cover_url || ""}
                onChange={(e) => setSong({ ...song, cover_url: e.target.value })}
              />
            </div>
            <button data-testid="song-save" onClick={saveSong} className="btn-primary">
              Save Song
            </button>
          </div>
        </div>
      )}

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto" onClick={close}>
          <div className="bg-[#121212] border border-[var(--gold)]/30 max-w-2xl w-full p-8 my-12" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-2xl font-bold uppercase">{editing === "new" ? "New Car" : "Edit Car"}</h3>
              <button onClick={close} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Title</label>
                <input data-testid="form-title" className="input-line" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Make</label>
                <input className="input-line" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Model</label>
                <input className="input-line" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Year</label>
                <input type="number" className="input-line" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Body Type</label>
                <select className="select-dark mt-1" value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })}>
                  {["Sedan", "Coupe", "SUV", "Convertible", "Hatchback"].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Transmission</label>
                <select className="select-dark mt-1" value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })}>
                  {["Automatic", "Manual"].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Fuel</label>
                <select className="select-dark mt-1" value={form.fuel} onChange={(e) => setForm({ ...form, fuel: e.target.value })}>
                  {["Petrol", "Diesel", "Electric", "Hybrid"].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Listing Type</label>
                <select data-testid="form-listing-type" className="select-dark mt-1" value={form.listing_type} onChange={(e) => setForm({ ...form, listing_type: e.target.value })}>
                  <option value="sale">For Sale</option>
                  <option value="rent">For Rent</option>
                </select>
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">
                  Price {form.listing_type === "rent" ? "(per day)" : ""}
                </label>
                <input data-testid="form-price" type="number" className="input-line" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Horsepower</label>
                <input type="number" className="input-line" value={form.horsepower} onChange={(e) => setForm({ ...form, horsepower: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Mileage (km)</label>
                <input type="number" className="input-line" value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Seats</label>
                <input type="number" className="input-line" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Location</label>
                <input className="input-line" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Description</label>
                <textarea rows={3} className="input-line resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Features (comma separated)</label>
                <input className="input-line" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-[0.65rem] uppercase tracking-widest text-gray-500">Image URLs (comma separated)</label>
                <input className="input-line" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={close} className="btn-secondary flex-1">Cancel</button>
              <button data-testid="form-save" onClick={save} className="btn-primary flex-1">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
